import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";

// Aug 2026 UX pass — item 12: a single reusable confirmation dialog for
// every handoff action across Sales/Finance/Operations (Sales -> Finance,
// Finance -> Operations, Operations -> Finance). Replaces any bare-click
// handoff trigger. Never computes eligibility itself — `summaryRows` and
// `receivingTeamSees` are always server-derived data the caller already
// fetched; this component only presents it and asks for explicit confirm.
//
// Props:
//   open, onOpenChange       — dialog visibility
//   title                    — e.g. "Hand off to Finance"
//   summaryRows              — [{ label, value }] "what's being handed off"
//   receivingTeamSees        — string OR string[] describing what the
//                              receiving team will see/do next
//   confirmLabel             — button label, e.g. "Confirm handoff"
//   onConfirm                — async () => void
//   busy                     — external busy state (optional, else internal)
//   tone                     — "default" | "warn" (amber accent for variance/risk)
export function HandoffConfirmDialog({
  open, onOpenChange, title = "Confirm handoff", summaryRows = [],
  receivingTeamSees, seesHeading = "What the receiving team will see",
  confirmLabel = "Confirm handoff", onConfirm, busy = false, tone = "default",
}) {
  const sees = Array.isArray(receivingTeamSees) ? receivingTeamSees : receivingTeamSees ? [receivingTeamSees] : [];
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-lg" data-testid="handoff-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-indigo-600" />{title}
          </DialogTitle>
          <DialogDescription className="sr-only">Confirm this handoff before it is sent to the receiving team.</DialogDescription>
        </DialogHeader>

        {summaryRows.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-x-4 gap-y-2" data-testid="handoff-summary">
            {summaryRows.map((row) => (
              <div key={row.label} className={row.wide ? "col-span-2" : ""}>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{row.label}</div>
                <div className="text-sm font-medium text-slate-800 font-mono-num">{row.value ?? "—"}</div>
              </div>
            ))}
          </div>
        )}

        {sees.length > 0 && (
          <div className={`rounded-lg border p-3 text-[13px] ${tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-indigo-200 bg-indigo-50 text-indigo-800"}`} data-testid="handoff-receiving-team">
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 opacity-80">{seesHeading}</div>
            <ul className="space-y-1 list-disc list-inside">
              {sees.map((line, idx) => <li key={idx}>{line}</li>)}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={onConfirm} disabled={busy} data-testid="handoff-confirm-submit" className="bg-indigo-600 hover:bg-indigo-700">
            {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}{confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
